/* istanbul ignore file */

import _ from 'underscore';
import {
  getFormattedImage,
  getFormattedThemeForApplying,
} from '@bingads-webui-campaign-react/copilot-common';
import {
  State,
  TemplateConfig,
  AdPropType,
  AdSize,
  Color,
  AssetProperties,
} from './types';
import {
  getNewAssetProperties,
  addBrandKitToRecommendedAssets,
  filterBrandKitLogos,
  mapBrandKitImagesToDisplayAd,
  sizeCategories,
  getAssetPropertiesFromBrandKitFonts,
  getBrandKitFilterLogos,
  getColorContrastRatio,
} from './utils';

export enum ActionTypes {
  updateAssetPropertyAndColor = 'UPDATE_ASSET_PROPERTIES_AND_COLOR',
  updateShortHeadLine = 'UPDATE_SHORT_EADLINE',
  updateLongHeadLine = 'UPDATE_LONG_HEADLINE',
  updateDescription = 'UPDATE_DESCRIPTION',
  updateCallToAction = 'UPDATE_CALL_TO_ACTION',
  updateImage = 'UPDATE_IMAGE',
  updateLogo = 'UPDATE_LOGO',
  updateAssetLoading = 'UPDATE_ASSET_LOADING',
  updateBackground = 'UPDATE_BACKGROUND',
  updateTargetSizes = 'UPDATE_TARGET_SIZES',
  updateColor = 'UPDATE_COLOR',
  updateAdImages = 'UPDATE_AD_IMAGES',
  setData = 'SET_BRAND_KIT_DATA',
  updateFinalUrl = 'UPDATE_FINAL_URL',
  updateBrandKit = 'UPDATE_BRAND_KIT',
  updateAds = 'UPDATE_ADS',
  updateRecommendedAssetsWithBrandKit = 'UPDATE_RECOMMENDED_ASSETS_WITH_BRAND_KIT',
  updateRecommendedAssets = 'UPDATE_RECOMMENDED_ASSETS',
}

export interface Action {
  type: ActionTypes;
  payload?: any;
  options?: any;
}

function propApplicable(sizeConfig: TemplateConfig, prop: AdPropType) {
  // TODO: handle all the cases
  if (prop === 'color') {
    return sizeConfig.color;
  }
  if (prop === 'assetProperties') {
    return true;
  }
  return sizeConfig[prop] && sizeConfig[prop].display;
}

function updateRecommendedAssetsWithBrandKitData(state: State, payload) {
  const { recommendedAssets, templateConfigs } = state;
  const newState = { ...state };
  newState.brandKit = payload;
  const assets = addBrandKitToRecommendedAssets(recommendedAssets, payload, templateConfigs);
  newState.recommendedAssets = assets;
  return newState;
}

function updateAdsWithBrandKit(state: State, payload) {
  const { targetSizes, templateConfigs } = state;
  const newState = { ...state };
  newState.brandKit = payload;

  const currentColors = Object.values(newState?.displayAds).map(displayAd => displayAd.color).flat();
  const currentColor = [...new Set(currentColors.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))

  let highestColor: string;

  if (payload?.palettes?.length > 0) {
    let colorRatio = 0;
    const textColors : string[] = [];
    const textColorStrings = ['ShortHeadlinse', 'LongHeadline', 'Description'];
    currentColor?.forEach(colorObject => {
      if (textColorStrings?.findIndex(name => name === colorObject.elementName) !== -1) {
        textColors.push(colorObject.color);
      }
    })

    if (textColors.length > 0) {
      for (let i = 0; i < payload?.palettes?.length; i += 1) {
        const color = payload.palettes[i].HexCode;
        const ratio = getColorContrastRatio(textColors, color);

        if (ratio >= 4.5 && ratio > colorRatio) {
          highestColor = color;
          colorRatio = ratio;
        }
      }
    }
  }

  targetSizes.forEach((size) => {
    const aspectRatioLogo = templateConfigs[size]?.logo?.aspectRatio ?? '';

    if (templateConfigs[size] && propApplicable(templateConfigs[size], 'image')) {
      if (payload?.images?.length > 0) {
        const displayImage = mapBrandKitImagesToDisplayAd(payload.images[0]);
        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            image: displayImage,
          },
        };
      }
    }

    if (templateConfigs[size] && propApplicable(templateConfigs[size], 'logo')) {
      const filterLogos = filterBrandKitLogos(payload, aspectRatioLogo);
      if (filterLogos?.length > 0) {
        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            logo: mapBrandKitImagesToDisplayAd(filterLogos[0], { aspectRatio: aspectRatioLogo }),
          },
        };
      }
    }

    if (templateConfigs[size] && propApplicable(templateConfigs[size], 'color') && currentColor) {
      const backgroundIndex = currentColor?.findIndex(color => color.elementName === 'Background') ?? -1;

      if (payload?.palettes?.length > 0 && backgroundIndex !== -1) {
        const newColors = [...currentColor];
        newColors[backgroundIndex] = {
          ...currentColor[backgroundIndex],
          color: highestColor,
        };

        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            color: newColors,
          },
        };
      }
    }

    if (templateConfigs[size] && propApplicable(templateConfigs[size], 'assetProperties')) {
      const updatedAssetProperties = getAssetPropertiesFromBrandKitFonts(payload.fonts);
      const newAssetProperties = getNewAssetProperties(
        newState.displayAds[size]?.assetProperties || [],
        updatedAssetProperties
      );
      newState.displayAds = {
        ...newState.displayAds,
        [size]: {
          ...newState.displayAds[size],
          assetProperties: newAssetProperties,
        },
      };
    }
  });

  if (state.recommendedAssets || !state.finalUrl) {
    const assets = addBrandKitToRecommendedAssets(state.recommendedAssets, payload, state.templateConfigs);
    newState.recommendedAssets = assets;
  }

  return newState;
}

function updateAdProp(state: State, payload, prop: AdPropType, options?: any) {
  const { targetSizes, templateConfigs } = state;
  const newState = { ...state };
  targetSizes.forEach((size) => {
    if (templateConfigs[size] && propApplicable(templateConfigs[size], prop)) {
      if (prop === 'image' || prop === 'logo') {
        const { cropSetting } = payload;
        const { keepCropSettings = false } = options || {};
        const newPayload = {
          ...newState.displayAds[size]?.[prop],
          ...payload,
          cropSetting: keepCropSettings
          ? newState.displayAds[size]?.[prop]?.cropSetting
          : cropSetting,
        };
        if (!keepCropSettings && !cropSetting) delete newPayload.cropSetting;
        // REMOVE CROP SETTING IF NOT IN PAYLOAD
        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            [prop]: newPayload,
          },
        };
      } else if (prop === 'background') {
        const currentColor = newState.displayAds[size]?.color;
        if (currentColor) {
          const index = currentColor.findIndex(color => color.elementName === 'Background');
          if (index !== -1) {
            const newColors = [...currentColor];
            newColors[index] = {
              ...currentColor[index],
              color: payload,
            };
            newState.displayAds = {
              ...newState.displayAds,
              [size]: {
                ...newState.displayAds[size],
                color: newColors,
              },
            };
          }
        }
      } else if (
        prop === 'cta' ||
        prop === 'shortHeadline' ||
        prop === 'longHeadline' ||
        prop === 'description'
      ) {
        const maxLength = templateConfigs[size][prop] ? templateConfigs[size][prop].maxLengthInChar : null;
        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            [prop]: {
              ...(newState.displayAds[size]?.[prop] || {}),
              text: maxLength ? payload.text.slice(0, maxLength) : payload.text,
            },
          },
        };
      } else if (prop === 'assetProperties') {
        const newAssetProperties = getNewAssetProperties(
          newState.displayAds[size]?.assetProperties || [],
          payload
        );
        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            assetProperties: newAssetProperties,
          },
        };
      } else {
        newState.displayAds = {
          ...newState.displayAds,
          [size]: {
            ...newState.displayAds[size],
            [prop]: payload,
          },
        };
      }
    }
  });

  return newState;
}

function updateColors(state: State, payload: Color) {
  const newState = { ...state };
  const { targetSizes, templateConfigs } = state;

  targetSizes.forEach((size) => {
    if (templateConfigs[size] && templateConfigs[size].color) {
      const currentColors = newState.displayAds[size]?.color || [];
      const updatedColors = [...currentColors];
      const index = currentColors.findIndex(color => color.elementName === payload.elementName);
      if (index === -1) {
        updatedColors.push({
          ...payload,
          editable: true,
          suffix: '',
        } as Color);
      } else {
        updatedColors[index] = {
          ...updatedColors[index],
          ...payload,
        };
      }
      newState.displayAds = {
        ...newState.displayAds,
        [size]: {
          ...newState.displayAds[size],
          color: updatedColors,
        },
      };
    }
  });

  return newState;
}

function updateAssetPropertiesAndColor(
  state: State,
  payload: {
    assetProperties?: AssetProperties[];
    color?: Color;
  }
) {
  let newState = { ...state };
  if (payload.assetProperties) {
    newState = updateAdProp(newState, payload.assetProperties, 'assetProperties');
  }
  if (payload.color) {
    newState = updateColors(newState, payload.color);
  }
  return newState;
}


function updateWithRecommendedAssets(state: State, assetsResponse, prop, options) {
  if (!assetsResponse || _.isEmpty(Object.keys(assetsResponse))) {
    return {
      ...state,
      isAssetsLoading: false,
    };
  }
  const {
    RecommendedAssets: {
      Themes,
      ShortHeadlines = [],
      LongHeadlines = [],
      Descriptions = [],
      ImageAssets = [],
    },
    Logo,
    Cta,
  } = assetsResponse;
  let newState = {
    ...state,
    isAssetsLoading: false,
  };
  let assets = {
    themes: _.map(Themes, getFormattedThemeForApplying),
    headlines: ShortHeadlines,
    longHeadlines: LongHeadlines,
    descriptions: Descriptions,
    imageAssets: _.map(ImageAssets, getFormattedImage),
    logo: _.map(Logo ? [{ Url: Logo }] : [], getFormattedImage),
    cta: Cta,
  };
  if (state.brandKit) {
    // check brandkit data is available or not.
    assets = addBrandKitToRecommendedAssets(assets, state.brandKit, state.templateConfigs);
  }
  newState[prop] = assets;
  const { hasUpdateTemplate } = options || {};
  // NOTE: We are also updating logo/image assets field with recommended assets only on first load
  if (hasUpdateTemplate && !state.brandKit?.images?.length) {
    const imageUrl = assets.imageAssets && assets.imageAssets.length > 0 ? assets.imageAssets[0].fullUrl : null;
    if (imageUrl) {
      newState = updateAdProp(newState, { url: imageUrl, imageSize: {} }, 'image');
    }
  }

  const { filterLogos } = getBrandKitFilterLogos(state.brandKit, state.templateConfigs);
  const logoUrl = hasUpdateTemplate && !filterLogos?.length && assets.logo && assets.logo.length > 0 ? assets.logo[0].fullUrl : null;
  if (logoUrl) {
    newState = updateAdProp(newState, { url: logoUrl, imageSize: {} }, 'logo');
  }
  // NOTE: We are also updating text assets field with recommended assets
  const recommendedHeadlineText = hasUpdateTemplate && assets.headlines && assets.headlines.length > 0 ? assets.headlines[0].Text : null;
  if (recommendedHeadlineText) {
    newState = updateAdProp(newState, { text: recommendedHeadlineText }, 'shortHeadline');
  }

  const recommendedLongHeadlineText = hasUpdateTemplate && assets.longHeadlines && assets.longHeadlines.length > 0 ? assets.longHeadlines[0].Text : null;
  if (recommendedLongHeadlineText) {
    newState = updateAdProp(newState, { text: recommendedLongHeadlineText }, 'longHeadline');
  }

  const recommendedDescriptionText = hasUpdateTemplate && assets.descriptions && assets.descriptions.length > 0 ? assets.descriptions[0].Text : null;
  if (recommendedDescriptionText) {
    newState = updateAdProp(newState, { text: recommendedDescriptionText }, 'description');
  }

  const recommendedCtaText = hasUpdateTemplate && assets.cta && assets.cta.length > 0 ? assets.cta[0].Text : null;
  if (recommendedCtaText) {
    newState = updateAdProp(newState, { text: recommendedCtaText }, 'cta');
  }

  return newState;
}

function filterSizes(state: State, filter: string | AdSize) {
  const newState = { ...state };
  if (filter === 'All sizes') {
    newState.targetSizes = [...state.adSizes];
  } else if (filter in sizeCategories) {
    newState.targetSizes = sizeCategories[filter];
  } else if (state.adSizes.includes(filter as AdSize)) {
    newState.targetSizes = [filter as AdSize];
  }
  newState.filter = filter;
  return newState;
}

export function reducer(state: State, action) {
  const { type, payload, options } = action;

  switch (type) {
    case ActionTypes.updateTargetSizes:
      return filterSizes(state, payload);
    case ActionTypes.updateAssetPropertyAndColor:
      return updateAssetPropertiesAndColor(state, payload);
    case ActionTypes.updateFinalUrl:
      return {
        ...state,
        finalUrl: payload,
      };
    case ActionTypes.updateRecommendedAssets:
      return updateWithRecommendedAssets(state, payload, 'recommendedAssets', options);
    case ActionTypes.updateShortHeadLine:
      return updateAdProp(state, payload, 'shortHeadline');
    case ActionTypes.updateLongHeadLine:
      return updateAdProp(state, payload, 'longHeadline');
    case ActionTypes.updateDescription:
      return updateAdProp(state, payload, 'description');
    case ActionTypes.updateCallToAction:
      return updateAdProp(state, payload, 'cta');
    case ActionTypes.updateImage:
      return updateAdProp(state, payload, 'image', options);
    case ActionTypes.updateLogo:
      return updateAdProp(state, payload, 'logo');
    case ActionTypes.updateBackground:
      return updateAdProp(state, payload, 'background');
    case ActionTypes.updateAssetLoading:
      return {
        ...state,
        isAssetsLoading: payload,
      };
    case ActionTypes.updateRecommendedAssetsWithBrandKit:
      return updateRecommendedAssetsWithBrandKitData(state, payload);
    case ActionTypes.updateColor:
      return updateColors(state, payload);
      case ActionTypes.updateAds:
        return {
          ...state,
          adImages: {
            ...state.adImages,
            ...payload.adImages,
          },
          displayAds: {
            ...state.displayAds,
            ...payload.displayAds,
          },
        };
    case ActionTypes.updateBrandKit:
      return updateAdsWithBrandKit(state, payload);
    case ActionTypes.updateAdImages:
      return {
        ...state,
        adImages: {
          ...state.adImages,
          ...payload,
        },
      };
    case ActionTypes.setData:
      return {
        ...state,
        ...payload,
      };
    default:
      return state;
  }
}
